<script setup lang="ts">
import { watch, computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { requiredIf } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '../../Shared/DatePicker.vue'

const props = defineProps<{
  disable?: boolean
}>()

const { umsokn, onLoaded } = useUmsokn()

const rules = computed(() => {
  const needsValidation = umsokn.value?.anotherPension === 'Já'

  return {
    umsokn: {
      anotherPensions: Array.isArray(umsokn.value?.anotherPensions)
        ? umsokn.value.anotherPensions.map(() => ({
            name: { required: requiredIf(() => needsValidation) },
            type: { required: requiredIf(() => needsValidation) },
            from: { required: requiredIf(() => needsValidation) },
            to: { required: requiredIf(() => needsValidation) }
          }))
        : [],
      vinnumalastofnunFrom: {
        required: requiredIf(() => umsokn.value?.vinnumalastofnun === 'Já')
      },
      vinnumalastofnunTo: {
        required: requiredIf(() => umsokn.value?.vinnumalastofnun === 'Já')
      },
      vinnumalastofnunAmount: {
        required: requiredIf(() => umsokn.value?.vinnumalastofnun === 'Já')
      }
    }
  }
})

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})

onLoaded(() => {
  if (umsokn.value) {
    if (!umsokn.value.anotherPensions || umsokn.value.anotherPensions.length === 0) {
      umsokn.value.anotherPensions = [
        { name: '', type: '', from: '', to: '' }
      ]
    }
  }
})

// auto-format vinnumalastofnunAmount
watch(
  () => umsokn.value?.vinnumalastofnunAmount,
  newVal => {
    if (newVal) {
      const digits = newVal.replace(/\D/g, '')
      umsokn.value.vinnumalastofnunAmount = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }
  }
)

function addPension() {
  umsokn.value.anotherPensions.push({
    name: '',
    type: '',
    from: '',
    to: ''
  })
}

function removePension(index: number) {
  umsokn.value.anotherPensions.splice(index, 1)
}

function todayISO() {
  return new Date().toISOString().split('T')[0]
}
</script>

<template>
  <div v-if="umsokn">
    <v-row>
      <v-col>
        <h2 class="form__header">{{ $t('ororku_umsokn_step4_header') }}</h2>
        <p style="font-weight:bold">{{ $t('ororku_umsokn_step4_header2') }}</p>
      </v-col>
    </v-row>

    <!-- Another pension radios -->
    <v-row>
      <v-col cols="12">
        <p>{{ $t('ororku_umsokn_step4_OtherInstitution') }}</p>
        <v-radio-group
          v-model="umsokn.anotherPension"
          :readonly="props.disable"
          inline
          hide-details
        >
          <v-radio :label="$t('ororku_umsokn_step3_yes')" value="Já" />
          <v-radio :label="$t('ororku_umsokn_step3_no')" value="Nei" />
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- Another pensions list -->
    <transition-group name="list">
      <div
        v-if="umsokn.anotherPension === 'Já'"
        v-for="(p, index) in umsokn.anotherPensions"
        :key="'anotherPension_' + index"
      >
        <div v-if="index > 0" class="forms__divider__container">
          <div class="forms__divider"></div>
          <button
            v-if="!props.disable"
            type="button"
            class="forms__warning__list__wrapper"
            @click="removePension(index)"
          >
            <img class="forms__warning__icon" src="@/assets/icons/Close.svg" alt="Remove" />
          </button>
        </div>

        <v-row>
          <v-col cols="12" md="4">
            <DatePicker
              v-model="p.from"
              :label="$t('ororku_umsokn_step4_from')"
              :disabled="props.disable"
              :max="todayISO()"
              :error="v$.umsokn.anotherPensions[index]?.from?.$error"
              :error-messages="v$.umsokn.anotherPensions[index]?.from?.$dirty
                ? [
                    v$.umsokn.anotherPensions[index]?.from?.required?.$invalid
                      ? $t('ororku_umsokn_step4_stettarfelagTo_error')
                      : ''
                  ].filter(Boolean)
                : []"
            />
          </v-col>

          <v-col cols="12" md="4">
            <DatePicker
              v-model="p.to"
              :label="$t('ororku_umsokn_step4_to')"
              :disabled="props.disable"
              :max="todayISO()"
              :error="v$.umsokn.anotherPensions[index]?.to?.$error"
              :error-messages="v$.umsokn.anotherPensions[index]?.to?.$dirty
                ? [
                    v$.umsokn.anotherPensions[index]?.to?.required?.$invalid
                      ? $t('ororku_umsokn_step4_stettarfelagTo_error')
                      : ''
                  ].filter(Boolean)
                : []"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="p.name"
              variant="outlined"
              density="comfortable"
              :disabled="props.disable"
              :label="$t('ororku_umsokn_step3_nafn')"
              :error="v$.umsokn.anotherPensions[index]?.name?.$error"
              :error-messages="v$.umsokn.anotherPensions[index]?.name?.$dirty
                ? [
                    v$.umsokn.anotherPensions[index]?.name?.required?.$invalid
                      ? $t('umsokn_umsaekjandi_nafn_error')
                      : ''
                  ].filter(Boolean)
                : []"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-radio-group
              v-model="p.type"
              :readonly="props.disable"
              hide-details
              :error="v$.umsokn.anotherPensions[index]?.type?.$error"
              :error-messages="v$.umsokn.anotherPensions[index]?.type?.$dirty
                ? [
                    v$.umsokn.anotherPensions[index]?.type?.required?.$invalid
                      ? $t('ororku_umsokn_step4_stettarfelagTo_error')
                      : ''
                  ].filter(Boolean)
                : []"
            >
              <v-radio
                :label="$t('ororku_umsokn_step4_anotherPensions_ororku')"
                value="Örorkulífeyri"
              />
              <v-radio
                :label="$t('ororku_umsokn_step4_anotherPensions_maka')"
                value="Makalífeyri"
              />
              <v-radio
                :label="$t('ororku_umsokn_step4_anotherPensions_other')"
                value="Annað"
              />
            </v-radio-group>
          </v-col>
        </v-row>
      </div>
    </transition-group>

    <div v-if="umsokn.anotherPension === 'Já' && !props.disable">
      <v-btn
        color="primary"
        variant="outlined"
        prepend-icon="mdi-plus"
        @click="addPension"
        class="mt-4 mb-10"
      >
        {{ $t('ororku_umsokn_step3_add') }}
      </v-btn>
    </div>

    <!-- Employer -->
    <v-row>
      <v-col cols="12">
        <p>{{ $t('ororku_umsokn_step4_employee') }}</p>
        <v-radio-group
          v-model="umsokn.employer"
          :readonly="props.disable"
          inline
          hide-details
        >
          <v-radio :label="$t('ororku_umsokn_step4_employee_yes')" value="Já" />
          <v-radio :label="$t('ororku_umsokn_step4_employee_no')" value="Nei" />
        </v-radio-group>
      </v-col>
    </v-row>

    <v-row v-if="umsokn.employer === 'Já'">
      <v-col cols="12" md="4">
        <DatePicker
          v-model="umsokn.employerStopped"
          :label="$t('ororku_umsokn_step4_employee_stops')"
          :readonly="props.disable"
          :max="todayISO()"
        />
      </v-col>
    </v-row>

    <!-- Vinnumálastofnun -->
    <v-row>
      <v-col cols="12">
        <p>{{ $t('ororku_umsokn_step4_stofnun') }}</p>
        <v-radio-group
          v-model="umsokn.vinnumalastofnun"
          :readonly="props.disable"
          inline
          hide-details="auto"
        >
          <v-radio :label="$t('ororku_umsokn_step4_stofnun_yes')" value="Já" />
          <v-radio :label="$t('ororku_umsokn_step4_stofnun_no')" value="Nei" />
        </v-radio-group>
      </v-col>
    </v-row>

    <div v-if="umsokn.vinnumalastofnun === 'Já'">
      <v-row>
        <v-col cols="12" md="4">
          <DatePicker
            v-model="umsokn.vinnumalastofnunFrom"
            :label="$t('ororku_umsokn_step4_from')"
            :disabled="props.disable"
            :max="todayISO()"
            :error="v$.umsokn.vinnumalastofnunFrom.$error"
            :error-messages="v$.umsokn.vinnumalastofnunFrom.$dirty
              ? [
                  v$.umsokn.vinnumalastofnunFrom?.required?.$invalid
                    ? $t('ororku_umsokn_step4_employerStoppedFrom_error')
                    : ''
                ].filter(Boolean)
              : []"
          />
        </v-col>

        <v-col cols="12" md="4">
          <DatePicker
            v-model="umsokn.vinnumalastofnunTo"
            :label="$t('ororku_umsokn_step4_to')"
            :disabled="props.disable"
            :max="todayISO()"
            :error="v$.umsokn.vinnumalastofnunTo.$error"
            :error-messages="v$.umsokn.vinnumalastofnunTo.$dirty
              ? [
                  v$.umsokn.vinnumalastofnunTo?.required?.$invalid
                    ? $t('ororku_umsokn_step4_employerStoppedTo_error')
                    : ''
                ].filter(Boolean)
              : []"
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-text-field
            v-model="umsokn.vinnumalastofnunAmount"
            variant="outlined"
            density="comfortable"
            :disabled="props.disable"
            :label="$t('ororku_umsokn_step4_stofnun_amount')"
            :error="v$.umsokn.vinnumalastofnunAmount.$error"
            :error-messages="v$.umsokn.vinnumalastofnunAmount.$dirty
              ? [
                  v$.umsokn.vinnumalastofnunAmount?.required?.$invalid
                    ? $t('ororku_umsokn_step4_vinnumalastofnunAmount_error')
                    : ''
                ].filter(Boolean)
              : []"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<style scoped lang="scss">
.forms__divider__container {
  margin: 24px 0;
  position: relative;
}

</style>